import type { EmployeePulseInsightsGroupsQuery } from 'generated/resources/graphql'
import { max, sum } from 'lodash'

import { getEmployeeMoodToolTip } from 'modules/engagement/components/EmployeePulseIcon'
import { GroupByType } from 'modules/performance_analytics/components/helpers'
import { useGroupByKeysLookupTransformer } from 'modules/performance_analytics/hooks/useGroupByKeysLookupTransformer'

import type {
  ChartDataItem,
  MOOD_CATEGORIES,
  ProcessedChartData,
} from 'modules/performance_analytics/components/EmployeePulseInsights/types'
import {
  type KeyCountPair,
  OTHER_CATEGORY_KEY,
  calculateMaxStackedValue,
  calculateTotalStackedCount,
  consolidateKeyCountPairs,
  extractFinalKeys,
  groupKeyCountPairs,
  normalizeGroupValue,
  sortPairsByCount,
} from './chartDataUtils'

const MOOD_SCORE_CATEGORIES = [1, 2, 3, 4, 5] as const
const DEFAULT_SERIES = [0, 0, 0, 0, 0] as const

const createRegularChartData = (series: readonly number[]): ChartDataItem[] => {
  return MOOD_SCORE_CATEGORIES.map((bucket, index) => ({
    bucket: getEmployeeMoodToolTip(bucket),
    score: series[index] ?? 0,
  }))
}

export const useProcessChartData = (
  data: EmployeePulseInsightsGroupsQuery | undefined,
  groupBy: GroupByType | undefined,
  moodCategories: typeof MOOD_CATEGORIES
): ProcessedChartData => {
  const showGroupBy = Boolean(groupBy)

  const { series, isEmpty } = processRawChartData(data)
  const regularChartData = createRegularChartData(series)

  // Default values for non-grouped data
  let stackedChartData: ChartDataItem[] = []
  let finalKeys: string[] = ['score']
  let maxOfGroupBy = max(series) ?? 0
  let totalCountOfStackedChartData = 0
  const {
    keys,
    loading: loadingLookups,
    rawToTransformedMap,
  } = useExtractUniqueValuesForGroupBy(data, groupBy, moodCategories)

  // Process grouped data if groupBy is enabled
  if (showGroupBy && groupBy) {
    stackedChartData = createStackedChartData(data, groupBy, keys, rawToTransformedMap)
    finalKeys = extractFinalKeys(stackedChartData)
    maxOfGroupBy = calculateMaxStackedValue(stackedChartData)
    totalCountOfStackedChartData = calculateTotalStackedCount(stackedChartData)
  }

  return {
    regularChartData,
    stackedChartData,
    finalKeys,
    maxOfGroupBy,
    totalCountOfStackedChartData,
    isEmpty: showGroupBy && groupBy ? totalCountOfStackedChartData === 0 : isEmpty,
    showGroupBy,
    loadingLookups,
  }
}

const processRawChartData = (
  data: EmployeePulseInsightsGroupsQuery | undefined
): {
  series: number[]
  isEmpty: boolean
} => {
  const { count, values } = data?.engagement?.employeePulseInsightsConnection?.aggregate?.terms
    ?.value ?? {
    count: [],
    values: [],
  }

  const series: number[] = Array.from(DEFAULT_SERIES)

  values?.forEach((category, index) => {
    const value = count?.[index]
    if (value && category) {
      const categoryIndex = Number(category) - 1
      if (categoryIndex >= 0 && categoryIndex < series.length) {
        series[categoryIndex] = value
      }
    }
  })

  const total = sum(series)
  const isEmpty = total === 0

  return { series, isEmpty }
}

const extractUniqueRawValues = (data: EmployeePulseInsightsGroupsQuery | undefined): string[] => {
  const uniqueRawValues = new Set<string>()
  const groupedNodes = data?.engagement?.employeePulseInsightGroupsConnection?.nodes || []

  groupedNodes.forEach((node) => {
    node.groups?.forEach((group) => {
      const normalizedGroup = normalizeGroupValue(group)
      uniqueRawValues.add(normalizedGroup)
    })
  })

  return Array.from(uniqueRawValues)
}

const createRawToTransformedMapping = (
  rawValues: string[],
  transformedValues: string[]
): Map<string, string> => {
  const mapping = new Map<string, string>()

  rawValues.forEach((rawValue, index) => {
    const transformedValue = transformedValues[index]
    if (transformedValue) {
      mapping.set(rawValue, transformedValue)
    }
  })

  return mapping
}

const useExtractUniqueValuesForGroupBy = (
  data: EmployeePulseInsightsGroupsQuery | undefined,
  groupBy: GroupByType | undefined,
  _moodCategories: typeof MOOD_CATEGORIES
): { keys: string[]; loading: boolean; rawToTransformedMap: Map<string, string> } => {
  const rawValues = extractUniqueRawValues(data)
  const { buckets, loading } = useGroupByKeysLookupTransformer(groupBy, rawValues)
  const rawToTransformedMap = createRawToTransformedMapping(rawValues, buckets)

  return {
    keys: buckets,
    loading,
    rawToTransformedMap,
  }
}

const createStackedChartData = (
  data: EmployeePulseInsightsGroupsQuery | undefined,
  groupBy: GroupByType,
  keys: readonly string[],
  rawToTransformedMap: Map<string, string>
): ChartDataItem[] => {
  return MOOD_SCORE_CATEGORIES.map((score) => {
    return createBucketData(
      getEmployeeMoodToolTip(score),
      String(score),
      keys,
      data,
      groupBy,
      rawToTransformedMap
    )
  })
}

const findMatchingNode = (
  data: EmployeePulseInsightsGroupsQuery | undefined,
  scoreValue: string
) => {
  const groupedNodes = data?.engagement?.employeePulseInsightGroupsConnection?.nodes || []
  return groupedNodes.find((node) => node.value === scoreValue)
}

const createTransformedKeyCountPairs = (
  groups: readonly string[],
  counts: readonly (number | null)[],
  rawToTransformedMap: Map<string, string>
): KeyCountPair[] => {
  return groups.map((group, index) => {
    const normalizedGroup = normalizeGroupValue(group)
    const transformedKey = rawToTransformedMap.get(normalizedGroup) || normalizedGroup
    return {
      key: transformedKey,
      count: counts[index] || 0,
    }
  })
}

const populateResultWithCounts = (
  result: Record<string, string | number>,
  keyCountPairs: KeyCountPair[]
): void => {
  const consolidatedPairs = consolidateKeyCountPairs(keyCountPairs)
  const sortedPairs = sortPairsByCount(consolidatedPairs)
  const { topKeys, otherCount } = groupKeyCountPairs(sortedPairs)

  topKeys.forEach(({ key, count }) => {
    result[key] = count
  })

  if (otherCount > 0) {
    result[OTHER_CATEGORY_KEY] = otherCount
  }
}

const populateResultWithZeros = (
  result: Record<string, string | number>,
  keys: readonly string[]
): void => {
  keys.forEach((key) => {
    result[key] = 0
  })
}

const createBucketData = (
  bucketValue: string,
  scoreValue: string,
  keys: readonly string[],
  data: EmployeePulseInsightsGroupsQuery | undefined,
  _groupBy: GroupByType,
  rawToTransformedMap: Map<string, string>
): ChartDataItem => {
  const result: Record<string, string | number> = {
    bucket: bucketValue,
  }

  const matchingNode = findMatchingNode(data, scoreValue)

  if (matchingNode?.groups && matchingNode.counts) {
    const keyCountPairs = createTransformedKeyCountPairs(
      matchingNode.groups,
      matchingNode.counts,
      rawToTransformedMap
    )
    populateResultWithCounts(result, keyCountPairs)
  } else {
    populateResultWithZeros(result, keys)
  }

  return result
}
